import React, { ReactNode, useEffect } from 'react';
import { get } from 'lodash';
import { withErrorBoundary } from 'react-error-boundary';
import { logger } from '@fiverr-private/obs';
import { Link, Typography } from '@fiverr-private/typography';
import { Stack } from '@fiverr-private/layout_components';
import { I18n } from '@fiverr-private/i18n-react';
import type { StackProps } from '@fiverr-private/layout_components/dist/esm/src/components/Stack/types';
import type { Gql } from '@fiverr-private/loyalty_utils';
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleHeader,
    CollapsibleHeaderChevron,
} from '@fiverr-private/disclosure';
import pathfinder from '@fiverr-private/pathfinder';
import { numberFormat } from '@fiverr-private/localization';
import { eventsApi } from '../../events';
import copies from '../../copies.json';
import { LoyaltyPointsIcon } from '../icons/LoyaltyPointsIcon';
import { LoyaltyTierIcon } from '../icons/LoyaltyTierIcon';
import { showLocalisedAmountOver } from './helpers';
import { useShouldShowBanner } from './hooks';
import { type BannerType, type PayloadGigInfo } from './bannerPropsTypes';

type Templates = NonNullable<Parameters<typeof I18n>[0]['templates']>;

interface LoyaltyIndicationTemplates extends Templates {
    loyaltyPageLink: (text?: string) => ReactNode;
}

interface LoyaltyIndicationTextParams {
    discount_percent: number;
    amount: string;
    sellerName?: string;
    agencyName?: string;
    points?: string | number;
}

interface LoyaltyIndicationBannerPointsModeComponentProps {
    type: BannerType;
    containerStyles?: StackProps & React.RefAttributes<HTMLElement>;
    loyaltyIndicationData: Gql.EngagementBadgeInfo;
    pointsAmount?: Gql.LoyaltyInfo['pointsAmount'];
    gigInfo: PayloadGigInfo;
    showTitleOnCollapseDisabled?: boolean;
    currency?: {
        rate: number;
        name: string;
    };
    isCollapsible?: boolean;
    defaultExpanded?: boolean;
    useTierIcon?: boolean;
}
interface TextProps {
    copyKey: string;
    params: LoyaltyIndicationTextParams;
    templates: LoyaltyIndicationTemplates;
}

const Title = ({ copyKey, params, templates }: TextProps) => (
    <Typography size="b_sm" color="grey_1200" fontWeight="bold" width="100%">
        <I18n k={`${copyKey}.title`} params={params} templates={templates} />
    </Typography>
);

const Subtitle = ({ copyKey, params, templates }: TextProps) => (
    <Typography size="b_sm" color="grey_1100">
        <I18n k={`${copyKey}.subtitle`} params={params} templates={templates} />
    </Typography>
);

const LoyaltyIndicationBannerPointsModeComponent = ({
    type,
    loyaltyIndicationData,
    gigInfo,
    currency,
    containerStyles = {},
    isCollapsible = false,
    defaultExpanded = false,
    pointsAmount,
    useTierIcon = false,
}: LoyaltyIndicationBannerPointsModeComponentProps) => {
    const shouldShow = useShouldShowBanner({
        gigInfo,
        loyaltyIndicationData,
        type,
    });

    useEffect(() => {
        if (shouldShow) {
            eventsApi.viewed_loyalty_credit_indication_badge({
                pageName: type,
                componentName: type,
                tierLevel: loyaltyIndicationData.tier,
            });
        }
    }, [loyaltyIndicationData.tier, shouldShow, type]);

    if (!shouldShow) {
        return null;
    }

    const { tier, creditBonusPercents, amountOver } = loyaltyIndicationData;
    const amount = showLocalisedAmountOver({
        amountOver: amountOver || 0,
        currency,
    });

    const params = {
        discount_percent: creditBonusPercents,
        amount,
        sellerName: gigInfo.sellerName,
        agencyName: gigInfo.agencyName,
        points: pointsAmount ? (numberFormat({ value: pointsAmount }) as string) : pointsAmount,
    };

    const templates: LoyaltyIndicationTemplates = {
        loyaltyPageLink: (text) => (
            <Link href={pathfinder('loyalty_dashboard_page')} fontWeight="semibold">
                {text}
            </Link>
        ),
    };

    const copyKeys: Record<BannerType, string> = {
        freelancer_page: `loyalty_indication.${type}.points.${loyaltyIndicationData.tier.toLowerCase()}`,
        listings: `loyalty_indication.${type}.points.${loyaltyIndicationData.tier.toLowerCase()}`,
        agency_page: `loyalty_indication.${type}.points.${loyaltyIndicationData.tier.toLowerCase()}`,
        gig_page: `loyalty_indication.${type}.points.${loyaltyIndicationData.tier.toLowerCase()}`,
        orders_page: `loyalty_indication.${type}.points`,
        payments_page: `loyalty_indication.${type}.points.${loyaltyIndicationData.tier.toLowerCase()}`,
        custom_offer_hourly: `loyalty_indication.${type}.points`,
    };

    const copyKey = copyKeys[type];

    const hasTitle = get(copies, `${copyKey}.title`);
    const hasSubtitle = get(copies, `${copyKey}.subtitle`);

    const canCollapse = isCollapsible && hasTitle && hasSubtitle;

    return (
        <Stack padding="4" borderWidth="sm" borderColor="grey_400" borderRadius="sm" {...containerStyles}>
            <Stack height="24px" alignItems="center">
                {useTierIcon ? (
                    <LoyaltyTierIcon tier={tier} size="md" />
                ) : (
                    <LoyaltyPointsIcon tier={tier} size="sm" type="multi" />
                )}
            </Stack>
            <Stack width="100%" direction="column" justifyContent="center">
                {hasTitle ? (
                    <Collapsible disabled={!canCollapse} defaultExpanded={defaultExpanded || !canCollapse}>
                        <CollapsibleHeader
                            flex="0 1 100%"
                            paddingTop="0"
                            height="24px"
                            alignContent="center"
                            paddingBottom="0"
                            width="100%"
                        >
                            <Title copyKey={copyKey} params={params} templates={templates} />
                            {canCollapse && <CollapsibleHeaderChevron />}
                        </CollapsibleHeader>

                        {hasSubtitle ? (
                            <CollapsibleContent>
                                <Subtitle copyKey={copyKey} params={params} templates={templates} />
                            </CollapsibleContent>
                        ) : (
                            <span />
                        )}
                    </Collapsible>
                ) : (
                    <Subtitle copyKey={copyKey} params={params} templates={templates} />
                )}
            </Stack>
        </Stack>
    );
};

export const LoyaltyIndicationBannerPointsMode = withErrorBoundary(LoyaltyIndicationBannerPointsModeComponent, {
    fallbackRender: () => null,
    onError: (error, componentStack) => {
        logger.error(error, {
            componentStack,
            description: 'An error was caught by an error boundary in LoyaltyIndicationBannerPointsMode',
        });
    },
});
